import { Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ClipboardList, ChevronDown } from "lucide-react";
import { useInquiry } from "@/lib/inquiry-store";
import { InquiryDrawer } from "./InquiryDrawer";
import { ProductSearch } from "./ProductSearch";

const NAV = [
  { to: "/collections", label: "Catalog" },

  { to: "/wholesale", label: "Wholesale" },
  { to: "/about", label: "About" },
  { to: "/private-label", label: "Private Label" },
  { to: "/certifications", label: "Certifications" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const BY_PRODUCT = [
  { handle: "Tea", label: "Tea", image: "/assets/images/tea-placeholder.jpg" },
  //{ handle: "Gift Selection", label: "Gift Selection", image: "/assets/images/mega-menu/gift-matcha.png" },
  //{ handle: "Sample Kits", label: "Sample Kits", image: "/assets/images/mega-menu/sample_kits_icon.png" },
  //{ handle: "Tea Ware", label: "Tea Ware", image: "/assets/images/mega-menu/A18-1-1.jpg" },
];

const BY_TEA_VARIETY = [
  { handle: "Matcha", label: "Matcha", image: "/assets/images/mega-menu/matcha_icon.png" },
  { handle: "Sencha", label: "Sencha", image: "/assets/images/mega-menu/sencha_icon.png" },
  { handle: "Kukicha", label: "Kukicha", image: "/assets/images/mega-menu/kukicha_icon.png" },
  { handle: "Gyokuro", label: "Gyokuro", image: "/assets/images/mega-menu/gyokuro_icon.png" },
  { handle: "Kabusecha", label: "Kabusecha", image: "/assets/images/mega-menu/kabusecha_icon.png" },
  { handle: "Hojicha", label: "Hojicha", image: "/assets/images/mega-menu/hojicha_powder.png" },
  {
    handle: "Black Tea",
    label: "Black Tea",
    image: "/assets/images/mega-menu/black_tea_icon_v2.png",
  },
];

const BY_FORMAT = [
  { handle: "Powder", label: "Powder", image: "/assets/images/categories/premium_icon.webp" },
  {
    handle: "Loose Leaf",
    label: "Loose Leaf",
    image: "/assets/images/mega-menu/loose_leaf_tea.png",
  },
  { handle: "Paste", label: "Paste", image: "/assets/images/categories/premium_icon.webp" },
];

const BY_GRADE = [
  {
    handle: "Ceremonial",
    label: "Ceremonial",
    image: "/assets/images/mega-menu/ceremonial_matcha.png",
  },
  { handle: "Premium", label: "Premium", image: "/assets/images/mega-menu/premium_icon.png" },
  { handle: "Culinary", label: "Culinary", image: "/assets/images/mega-menu/culinary_matcha.png" },
];

const BY_ORIGIN = [
  { handle: "Shizuoka", label: "Shizuoka", image: "/assets/images/mega-menu/yamae_shizuoka.png" },
  { handle: "Yame", label: "Yame", image: "/assets/images/mega-menu/yamaei_fukuoka.png" },
  { handle: "Uji", label: "Uji", image: "/assets/images/mega-menu/uji.png" },
  { handle: "Kagoshima", label: "Kagoshima", image: "/assets/images/mega-menu/yamaei_fukuoka.png" },
  { handle: "Kyushu", label: "Kyushu", image: "/assets/images/mega-menu/yamaei_fukuoka.png" },
];

const BY_CULTIVATION = [
  { handle: "Organic", label: "Organic", image: "/assets/images/mega-menu/organic.png" },
  { handle: "Conventional", label: "Conventional", image: "/assets/images/mega-menu/classic.png" },
];

const BY_COLOR = [
  { handle: "Vibrant Green", label: "Vibrant Green", colorCode: "#9dcc00" },
  { handle: "Deep Green", label: "Deep Green", colorCode: "#488540" },
  { handle: "Dark Green", label: "Dark Green", colorCode: "#254520" },
  { handle: "Green", label: "Green", colorCode: "#488540" },
  { handle: "Light Green", label: "Light Green", colorCode: "#a3c865" },
];

const BY_HARVEST_2025 = [{ handle: "Available", label: "Available in 2025" }];

const BY_HARVEST_2026 = [{ handle: "Available", label: "Available in 2026" }];

export function Header() {
  const { count } = useInquiry();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [drawer, setDrawer] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-[21px] lg:px-8">
        <div className="flex flex-1">
          <Link to="/" className="flex items-center gap-3" onClick={() => setMobile(false)}>
            <img
              src="/assets/images/logo.webp"
              alt="QT Foods"
              width={300}
              height={100}
              className="h-9 w-[108px] md:h-10 md:w-[120px]"
            />
          </Link>
        </div>
        <nav className="hidden items-center justify-center md:gap-4 lg:gap-6 xl:gap-8 md:flex">
          <div
            className="relative py-6 -my-6 px-4 -mx-4 flex items-center justify-center"
            onMouseEnter={() => setProductsOpen(true)}
            onMouseLeave={() => setProductsOpen(false)}
          >
            <button
              type="button"
              className="group relative inline-flex items-center gap-1 text-[15.5px] font-medium tracking-wide text-foreground/80 transition-colors hover:text-primary whitespace-nowrap"
              onClick={() => setProductsOpen((v) => !v)}
              aria-expanded={productsOpen}
            >
              Products
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </button>
            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3"
                >
                  <div className="grid w-[1000px] grid-cols-5 gap-x-8 gap-y-8 border border-border bg-background p-6 shadow-lg">
                    <div className="space-y-8">
                      <div>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Product
                        </p>
                        <ul className="space-y-3">
                          {BY_PRODUCT.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ product: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <p className="mb-3 mt-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Cultivation
                        </p>
                        <ul className="space-y-3">
                          {BY_CULTIVATION.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ cultivation: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Tea Variety
                        </p>
                        <ul className="space-y-3">
                          {BY_TEA_VARIETY.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ tea_type: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <p className="mb-3 mt-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Harvest
                        </p>
                        <ul className="space-y-3">
                          <li>
                            <Link
                              to="/collections"
                              search={{ harvest_2025: "Available" }}
                              className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                              onClick={() => setProductsOpen(false)}
                            >
                              2025 Harvest
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/collections"
                              search={{ harvest_2026: "Available" }}
                              className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                              onClick={() => setProductsOpen(false)}
                            >
                              2026 Harvest
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Format
                        </p>
                        <ul className="space-y-3 mb-8">
                          {BY_FORMAT.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ format: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Grade
                        </p>
                        <ul className="space-y-3">
                          {BY_GRADE.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ grade: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Origin
                        </p>
                        <ul className="space-y-3 mb-8">
                          {BY_ORIGIN.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ origin: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                {c.image && (
                                  <img
                                    src={c.image}
                                    alt={c.label}
                                    width={30}
                                    height={30}
                                    className="h-[30px] w-[30px] rounded-full object-cover"
                                    loading="lazy"
                                  />
                                )}
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div>
                        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          Color
                        </p>
                        <ul className="space-y-3 mb-8">
                          {BY_COLOR.map((c) => (
                            <li key={c.handle}>
                              <Link
                                to="/collections"
                                search={{ color: c.handle }}
                                className="flex items-center gap-3 text-[17px] text-foreground/80 hover:text-primary"
                                onClick={() => setProductsOpen(false)}
                              >
                                <span
                                  className="h-[20px] w-[20px] rounded-full shadow-sm"
                                  style={{ backgroundColor: c.colorCode }}
                                ></span>
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="group relative text-[15.5px] font-medium tracking-wide text-foreground/80 transition-colors hover:text-primary whitespace-nowrap"
              activeProps={{ className: "text-primary" }}
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full"></span>
            </Link>
          ))}
        </nav>
        <div className="flex flex-1 items-center justify-end gap-2">
          {!isHomePage && <ProductSearch />}
          <button
            type="button"
            onClick={() => setDrawer(true)}
            className="relative inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15.5px] font-medium tracking-wide text-primary-foreground shadow-sm transition-all hover:bg-primary/90 whitespace-nowrap min-w-[110px] justify-center"
            aria-label="Open inquiry list"
          >
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Inquiry</span>
            {count > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-background px-1.5 text-xs font-semibold text-primary">
                {count}
              </span>
            )}
          </button>
          <button
            type="button"
            className="md:hidden rounded-sm border border-border p-2"
            onClick={() => setMobile((m) => !m)}
            aria-label="Toggle menu"
          >
            {mobile ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {mobile && (
        <nav className="max-h-[80vh] overflow-y-auto border-t border-border md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col px-5 py-3">
            <button
              type="button"
              className="flex items-center justify-between py-3 text-[17px] tracking-wide text-foreground/80"
              onClick={() => setMobileProducts((v) => !v)}
            >
              Products
            </button>
            {mobileProducts && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-3 pl-3 pt-2">
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Product
                  </p>
                  <div className="space-y-2">
                    {BY_PRODUCT.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ product: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                  </div>
                  <p className="mb-2 mt-5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Cultivation
                  </p>
                  <div className="space-y-2">
                    {BY_CULTIVATION.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ cultivation: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Tea Variety
                  </p>
                  <div className="space-y-2">
                    {BY_TEA_VARIETY.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ tea_type: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                  </div>
                  <p className="mb-2 mt-5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Harvest
                  </p>
                  <div className="space-y-2">
                    <Link
                      to="/collections"
                      search={{ harvest_2025: "Available" }}
                      className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                      onClick={() => setMobile(false)}
                    >
                      2025 Harvest
                    </Link>
                    <Link
                      to="/collections"
                      search={{ harvest_2026: "Available" }}
                      className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                      onClick={() => setMobile(false)}
                    >
                      2026 Harvest
                    </Link>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Format & Grade
                  </p>
                  <div className="space-y-2">
                    {BY_FORMAT.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ format: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                    {BY_GRADE.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ grade: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Origin
                  </p>
                  <div className="space-y-2">
                    {BY_ORIGIN.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ origin: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        {c.image && (
                          <img
                            src={c.image}
                            alt={c.label}
                            width={30}
                            height={30}
                            className="h-[30px] w-[30px] rounded-full object-cover"
                            loading="lazy"
                          />
                        )}
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Color
                  </p>
                  <div className="space-y-2">
                    {BY_COLOR.map((c) => (
                      <Link
                        key={c.handle}
                        to="/collections"
                        search={{ color: c.handle }}
                        className="flex items-center gap-2 py-1 text-[17px] text-foreground/80"
                        onClick={() => setMobile(false)}
                      >
                        <span
                          className="h-[20px] w-[20px] rounded-full shadow-sm"
                          style={{ backgroundColor: c.colorCode }}
                        ></span>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="py-3 text-[17px] tracking-wide text-foreground/80"
                onClick={() => setMobile(false)}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
      <InquiryDrawer open={drawer} onOpenChange={setDrawer} />
    </header>
  );
}
